import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { cloneDeep } from 'lodash-es';
import { Store } from '@ngxs/store';
import { FilterGroup } from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/filter-group.model';
import { FilterGroupValue } from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/filter-group-value.model';
import { Filter } from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/filter.model';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { DateTimeRangeValue } from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/filter-datetime-range.model';
import { DurationValue } from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/filter-duration.model';
import { NumberRangeValue } from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/filter-number-range.model';
import { FilterMenuEvent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-menu/nimbus-filter-menu.component';
import { LocalizationState } from '@nimbus-library/features/localization/store/localization.state';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-nimbus-filter-group',
  templateUrl: './nimbus-filter-group.component.html',
  styleUrls: ['./nimbus-filter-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NimbusFilterGroupComponent implements OnInit, OnChanges {
  @Input()
  public compactView: boolean = false;
  @Input()
  public filterGroup: FilterGroup;
  @Input()
  public disabled: boolean = false;
  @Input()
  public filterGroupValue: FilterGroupValue;
  @Input()
  public locale?: string;

  @Output()
  public filterGroupValueChange = new EventEmitter<FilterGroupValue>();
  @Output()
  public selectFilterSearchValueChanged = new EventEmitter<{ id: string; val: string }>();

  @HostBinding('class.no-filter-elements')
  public noFilterSelected = false;

  private filterGroupValueSubject = new Subject<FilterGroupValue>();

  public readonly DEFAULT_COMPACT_FILTERS_COUNT = 2;

  public filterOptions: Filter[];
  public filterOptionsSelected: Filter[];
  public filterOptionsVisible: Filter[];
  public lastAdded: string;
  public showAllFilterInCompactView: boolean = false;
  public isFilterMenuOpened: boolean = false;
  public selectedDateFormat$: Observable<string>;

  public readonly ICONS = ICONS;

  public constructor(private readonly store: Store) {}

  public trackByFn = (_: number, item: Filter): string => item.id;

  public ngOnInit(): void {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    this.filterGroupValueSubject.pipe(debounceTime(500), untilDestroyed(this)).subscribe((filterGroupValue) => {
      this.filterGroupValueChange.emit(filterGroupValue);
    });

    this.selectedDateFormat$ = this.locale ? of(this.locale) : this.store.select(LocalizationState.selectedDateFormat);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.filterGroup || changes.filterGroupValue) {
      this.calculateFilterVisibility();
    }

    if (changes.filterGroup) {
      this.lastAdded = null;
    }

    if (changes.compactView) {
      this.showAllFilterInCompactView = false;
      this.calculateFilterVisibility();
    }
  }

  public onSelectSearchControlValueChanged(id: string, val: string): void {
    this.selectFilterSearchValueChanged.emit({ id, val });
  }

  public onFilterSelected({ filter, isMenuOpened }: FilterMenuEvent): void {
    this.isFilterMenuOpened = isMenuOpened;

    if (this.filterGroupValue[filter.id]) {
      this.onFilterValueChanged(filter.id, null);
      return;
    }

    this.lastAdded = filter.id;
    this.showAllFilterInCompactView = true;
    this.onFilterValueChanged(filter.id, filter.defaultValue);
  }

  public onFilterValueChanged(id: string, value: string[] | DateTimeRangeValue | DurationValue | NumberRangeValue): void {
    this.filterGroupValue = { ...this.filterGroupValue, [id]: cloneDeep(value) };
    this.calculateFilterVisibility();
    this.filterGroupValueSubject.next(this.filterGroupValue);
  }

  public toggleFilters(): void {
    this.showAllFilterInCompactView = !this.showAllFilterInCompactView;
    this.calculateFilterVisibility();
  }

  public isFilterOpenedOnInit(filterId: string): boolean {
    return this.lastAdded === filterId && !this.isFilterMenuOpened;
  }

  private calculateFilterVisibility(): void {
    this.filterOptions = this.filterGroup?.filters;
    this.filterOptionsSelected = this.filterGroup?.filters?.filter((x) => this.filterGroupValue?.[x.id]) || [];
    this.filterOptionsVisible =
      !this.showAllFilterInCompactView && this.compactView
        ? this.filterOptionsSelected?.slice(0, this.DEFAULT_COMPACT_FILTERS_COUNT)
        : this.filterOptionsSelected;
    this.noFilterSelected = this.filterOptionsSelected.length === 0 && this.compactView;
  }
}
